import { RecentOrder, SalesConfig, StoreGrade, StoreSalesInfoDoc } from '@gooduncles/gu-app-schema';
import { ICellRendererParams, RowStyle } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ColDef } from 'ag-grid-enterprise';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Badge, Button, Checkbox, DatePicker, Input, Modal, Select, Table, message } from 'antd';
import type { PresetStatusColorType } from 'antd/es/_util/colors';
import dayjs from 'dayjs';
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  formatNumberForAgCell,
  loadGridState,
  onCellValueChangedWithUpdate,
  onValueSetterWithValidation,
} from 'src/utils/ag-grid-util';
import { formatNumber } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';
import { updateStoreSalesInfo } from 'src/utils/firebase/firebase-short-cut';

import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';
import { AgCellStoreState } from 'src/atomic-components/atoms/AgCell/AgCellStoreState';
import AgGridSettingsButtons from 'src/atomic-components/molecules/AgGridSettingsButtons/AgGridSettingsButtons';

import { getLeadSourceAgentChildren, getLeadSourceChildren, getLeadSourceOptions } from './SalesManagement';
import StoreDetailModal from './StoreDetailModal';

export const SALES_MANAGEMENT_TABLE_ID = 'sales-management-table';

const STORE_SALES_INFO_COLLECTION = 'storeSalesInfo';

const defaultSetupStatusBadgeMap: Record<string, PresetStatusColorType> = {
  대기: 'processing',
  가망: 'processing',
  부재: 'warning',
  '2회 부재': 'error',
  보류: 'default',
  중지: 'default',
  배송불가: 'error',
  탈퇴: 'default',
  완료: 'success',
};

const disabledUISetupStatuses = ['중지', '배송불가', '탈퇴'];

function getRowStyle(params: { data?: StoreSalesInfoDoc }): RowStyle | undefined {
  if (!params.data) return undefined;
  if (disabledUISetupStatuses.includes(params.data.setupStatus)) {
    return { backgroundColor: '#f5f5f5', color: '#bfbfbf', opacity: '0.6' };
  }
  if (params.data.setupStatus === '완료') {
    return { backgroundColor: '#f6ffed' };
  }
  return undefined;
}

interface CellRendererWithConfigProps extends ICellRendererParams<StoreSalesInfoDoc> {
  salesConfig: SalesConfig;
}

const SetupStatusCellRenderer: FC<CellRendererWithConfigProps> = (params) => {
  const { data, value, salesConfig } = params;

  if (!data) return null;

  const statusOptions =
    salesConfig.setupStatuses.length > 0 ? salesConfig.setupStatuses : Object.keys(defaultSetupStatusBadgeMap);

  const handleChange = async (newValue: string) => {
    if (newValue === value) return;
    await updateStoreSalesInfo(data._id, { setupStatus: newValue });
  };

  return (
    <Select
      value={value}
      onChange={handleChange}
      options={statusOptions.map((s) => ({ value: s, label: s }))}
      size='small'
      style={{ width: '100%' }}
      variant='borderless'
      labelRender={() => <Badge status={defaultSetupStatusBadgeMap[value] ?? 'default'} text={value} />}
      optionRender={(option) => (
        <Badge status={defaultSetupStatusBadgeMap[option.value as string] ?? 'default'} text={option.label} />
      )}
    />
  );
};

const LeadSourceCellRenderer: FC<CellRendererWithConfigProps> = (params) => {
  const { data, value, api, node, salesConfig } = params;

  if (!data) return null;

  const options = getLeadSourceOptions(salesConfig.leadSourceTree).map((label) => ({
    value: label,
    label,
  }));

  const updateLeadSource = (leadSource: string | null) => {
    const update = { leadSource, leadSourceDetail: null, leadSourceAgent: null };
    updateStoreSalesInfo(data._id, update);
    node.setData({ ...data, ...update });
    api.refreshCells({ rowNodes: [node], columns: ['leadSourceDetail', 'leadSourceAgent'], force: true });
  };

  return (
    <Select
      value={value}
      onChange={(newValue) => {
        if (newValue !== value) updateLeadSource(newValue);
      }}
      options={options}
      size='small'
      style={{ width: '100%' }}
      variant='borderless'
      allowClear
      onClear={() => updateLeadSource(null)}
      placeholder='-'
    />
  );
};

/** leadSource에 따라 Select(고정 선택지) 또는 자유 텍스트 Input을 렌더링 */
const LeadSourceDetailRenderer: FC<CellRendererWithConfigProps> = (params) => {
  const { data, value, api, node, salesConfig } = params;
  const [textValue, setTextValue] = useState<string>(value ?? '');

  if (!data) return null;

  const { leadSource } = data;

  if (!leadSource) {
    return <span style={{ color: '#8c8c8c' }}>-</span>;
  }

  const children = getLeadSourceChildren(salesConfig.leadSourceTree, leadSource);

  // 고정 선택지
  if (children !== null) {
    const options = children.map((child) => ({
      value: child.label,
      label: child.label,
    }));

    const updateLeadSourceDetail = (leadSourceDetail: string | null) => {
      const update = { leadSourceDetail, leadSourceAgent: null };
      updateStoreSalesInfo(data._id, update);
      node.setData({ ...data, ...update });
      api.refreshCells({ rowNodes: [node], columns: ['leadSourceAgent'], force: true });
    };

    return (
      <Select
        value={value}
        onChange={updateLeadSourceDetail}
        options={options}
        size='small'
        style={{ width: '100%' }}
        variant='borderless'
        allowClear
        onClear={() => updateLeadSourceDetail(null)}
        placeholder='-'
      />
    );
  }

  // 자유 입력
  return (
    <Input
      value={textValue}
      onChange={(e) => setTextValue(e.target.value)}
      onBlur={() => {
        const newValue = textValue.trim() || null;
        if (newValue !== value) {
          updateStoreSalesInfo(data._id, { leadSourceDetail: newValue });
        }
      }}
      size='small'
      variant='borderless'
      placeholder='직접 입력'
    />
  );
};

const LeadSourceAgentCellRenderer: FC<CellRendererWithConfigProps> = (params) => {
  const { data, value, salesConfig } = params;
  const [textValue, setTextValue] = useState<string>(value ?? '');

  if (!data) return null;

  const { leadSource, leadSourceDetail } = data;

  if (!leadSource || !leadSourceDetail) {
    return <span style={{ color: '#8c8c8c' }}>-</span>;
  }

  const agentChildren = getLeadSourceAgentChildren(salesConfig.leadSourceTree, leadSource, leadSourceDetail);

  // 고정 선택지
  if (agentChildren !== null) {
    const options = agentChildren.map((child) => ({
      value: child.label,
      label: child.label,
    }));

    return (
      <Select
        value={value}
        onChange={(newValue) => updateStoreSalesInfo(data._id, { leadSourceAgent: newValue ?? null })}
        options={options}
        size='small'
        style={{ width: '100%' }}
        variant='borderless'
        allowClear
        onClear={() => updateStoreSalesInfo(data._id, { leadSourceAgent: null })}
        placeholder='-'
      />
    );
  }

  // 자유 입력
  return (
    <Input
      value={textValue}
      onChange={(e) => setTextValue(e.target.value)}
      onBlur={() => {
        const newValue = textValue.trim() || null;
        if (newValue !== value) {
          updateStoreSalesInfo(data._id, { leadSourceAgent: newValue });
        }
      }}
      size='small'
      variant='borderless'
      placeholder='직접 입력'
    />
  );
};

/** 여러 boolean 필드에 재사용되는 Checkbox cellRenderer */
const CheckboxCellRenderer: FC<ICellRendererParams<StoreSalesInfoDoc>> = (params) => {
  const { data, value, colDef } = params;

  if (!data || !colDef?.field) return null;

  const field = colDef.field as keyof StoreSalesInfoDoc;
  const isAllSetupComplete =
    data.isKakaoChannelAdded && data.isCheckedBusinessInfo && data.isServiceGuideProvided && data.isFollowUpNeeded;

  const handleChange = async (checked: boolean) => {
    try {
      const update = isAllSetupComplete ? { [field]: checked, setupStatus: '완료' } : { [field]: checked };
      await updateStoreSalesInfo(data._id, update);
      message.success('변경완료');
    } catch (error) {
      console.error('Failed to update:', error);
      message.error('변경에 실패했습니다.');
    }
  };

  return <Checkbox checked={!!value} onChange={(e) => handleChange(e.target.checked)} />;
};

const ReadOnlyCheckboxRenderer: FC<ICellRendererParams<StoreSalesInfoDoc>> = (params) => {
  if (!params.data) return null;
  return <Checkbox checked={!!params.value} disabled />;
};

const LastContactDateRenderer: FC<ICellRendererParams<StoreSalesInfoDoc>> = (params) => {
  const { data, value } = params;

  if (!data) return null;

  const handleChange = async (date: dayjs.Dayjs | null) => {
    const isoDate = date ? date.format('YYYY-MM-DD[T]HH:mm:ss+0900') : null;
    await updateStoreSalesInfo(data._id, { lastContactAt: isoDate });
  };

  return (
    <DatePicker
      value={value ? dayjs(value) : null}
      onChange={handleChange}
      size='small'
      variant='borderless'
      style={{ width: '100%' }}
      format='YYYY-MM-DD'
    />
  );
};

const storeGradeOptions: { value: StoreGrade; label: string }[] = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
  { value: 'D', label: 'D' },
];

const StoreGradeCellRenderer: FC<ICellRendererParams<StoreSalesInfoDoc>> = (params) => {
  const { data, value } = params;
  if (!data) return null;

  const handleChange = async (newValue: StoreGrade | undefined) => {
    await updateStoreSalesInfo(data._id, { storeGrade: newValue ?? null });
  };

  return (
    <Select
      value={value ?? undefined}
      onChange={handleChange}
      options={storeGradeOptions}
      size='small'
      style={{ width: '100%' }}
      variant='borderless'
      allowClear
      onClear={() => updateStoreSalesInfo(data._id, { storeGrade: null })}
      placeholder='-'
    />
  );
};

interface SalesManagementTableProps {
  rowData: StoreSalesInfoDoc[];
  salesConfig: SalesConfig;
  toolbarLeftContent?: ReactNode;
  toolbarRightContent?: ReactNode;
}

const onCellValueChanged = onCellValueChangedWithUpdate<StoreSalesInfoDoc>('영업관리');

/** recentOrders 모달 테이블 컬럼 */
const recentOrderColumns = [
  {
    title: '배송일',
    dataIndex: 'date',
    key: 'date',
    width: 120,
  },
  {
    title: '주문 금액',
    dataIndex: 'amount',
    key: 'amount',
    width: 120,
    align: 'right' as const,
    render: (value: number) => `${formatNumber(value, 0)}원`,
  },
];

const SalesManagementTable: FC<SalesManagementTableProps> = ({
  rowData,
  salesConfig,
  toolbarLeftContent,
  toolbarRightContent,
}) => {
  const [gridRef, setGridRef] = useState<AgGridReact<StoreSalesInfoDoc> | null>(null);
  const [recentOrdersModal, setRecentOrdersModal] = useState<{
    storeName: string;
    orders: RecentOrder[];
  } | null>(null);
  const [storeDetailModal, setStoreDetailModal] = useState<{
    storeName: string;
    storeId: string;
  } | null>(null);

  const handleCloseRecentOrders = useCallback(() => {
    setRecentOrdersModal(null);
  }, []);

  const handleOpenStoreDetail = useCallback((storeId: string, storeName: string) => {
    setStoreDetailModal({ storeName, storeId });
  }, []);

  const handleCloseStoreDetail = useCallback(() => {
    setStoreDetailModal(null);
  }, []);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
    }),
    []
  );

  const columnDefs = useMemo<ColDef<StoreSalesInfoDoc>[]>(
    () => [
      {
        headerName: '가입일',
        field: 'storeCreatedAt',
        width: 100,
        valueFormatter: ({ value }) => (value ? formatDate(value, 'MM-dd') : ''),
        sort: 'desc',
      },
      {
        headerName: '매장 상태',
        field: 'state',
        width: 110,
        cellRenderer: AgCellStoreState,
      },
      {
        headerName: '매장명',
        field: 'storeName',
        width: 180,
        cellRenderer: (params: ICellRendererParams<StoreSalesInfoDoc>) => {
          const { data, value } = params;
          if (!data) return null;
          return (
            <Button
              type='link'
              size='small'
              style={{ padding: 0 }}
              onClick={() => handleOpenStoreDetail(data._id, data.storeName)}>
              {value}
            </Button>
          );
        },
      },
      {
        headerName: '진행 상태',
        field: 'setupStatus',
        width: 160,
        cellRenderer: SetupStatusCellRenderer,
        cellRendererParams: { salesConfig },
      },
      {
        headerName: '유입 경로',
        field: 'leadSource',
        width: 180,
        cellRenderer: LeadSourceCellRenderer,
        cellRendererParams: { salesConfig },
      },
      {
        headerName: '유입 상세',
        field: 'leadSourceDetail',
        width: 180,
        cellRenderer: LeadSourceDetailRenderer,
        cellRendererParams: { salesConfig },
      },
      {
        headerName: '유입 담당자',
        field: 'leadSourceAgent',
        width: 120,
        cellRenderer: LeadSourceAgentCellRenderer,
        cellRendererParams: { salesConfig },
      },
      {
        headerName: '카카오',
        field: 'isKakaoChannelAdded',
        width: 100,
        cellRenderer: CheckboxCellRenderer,
      },
      {
        headerName: '사업자',
        field: 'isCheckedBusinessInfo',
        width: 100,
        cellRenderer: CheckboxCellRenderer,
      },
      {
        headerName: '서비스 안내',
        field: 'isServiceGuideProvided',
        width: 120,
        cellRenderer: CheckboxCellRenderer,
      },
      {
        headerName: '후속 통화',
        field: 'isFollowUpNeeded',
        width: 120,
        cellRenderer: CheckboxCellRenderer,
      },
      {
        headerName: '최초 배송일',
        field: 'firstDeliveryDate',
        width: 120,
      },
      {
        headerName: '최근 배송일',
        width: 120,
        valueGetter: (params) => params.data?.recentOrders?.[0]?.date ?? null,
        valueFormatter: ({ value }) => (value ? formatDate(value, 'MM-dd') : ''),
        cellStyle: (params) => {
          if (!params.value) return undefined;
          const diff = dayjs().diff(dayjs(params.value), 'day');
          if (diff >= 60) return { backgroundColor: '#f8d7da' };
          if (diff >= 30) return { backgroundColor: '#fce5d4' };
          if (diff >= 7) return { backgroundColor: '#fff3cd' };
          return undefined;
        },
      },
      {
        headerName: '배송지 상세',
        headerTooltip: '*서버에서 자동으로 채워집니다.',
        field: 'isDeliveryInfoProvided',
        width: 120,
        cellRenderer: ReadOnlyCheckboxRenderer,
      },
      {
        headerName: '마지막 컨택',
        field: 'lastContactAt',
        width: 160,
        cellRenderer: LastContactDateRenderer,
      },
      {
        headerName: '메모',
        field: 'note',
        width: 200,
        wrapText: true,
        autoHeight: true,
        cellStyle: { whiteSpace: 'pre-wrap', lineHeight: '1.5', padding: 8 } as any,
        editable: true,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        tooltipField: 'note',
        valueSetter: (params) =>
          onValueSetterWithValidation(params, undefined, undefined, { nullable: true, nullify: true }),
        onCellValueChanged: (params) =>
          onCellValueChanged(params, STORE_SALES_INFO_COLLECTION, undefined, undefined, false, true),
      },
      {
        headerName: '객단가',
        field: 'avgOrderAmount',
        width: 140,
        type: 'numericColumn',
        valueFormatter: formatNumberForAgCell,
        cellRenderer: (params: ICellRendererParams<StoreSalesInfoDoc>) => {
          if (!params.data) return null;
          const { avgOrderAmount, recentOrders, storeName } = params.data;
          if (avgOrderAmount == null) return '-';
          if (recentOrders && recentOrders.length > 0) {
            return (
              <Button
                type='link'
                size='small'
                style={{ padding: 0 }}
                onClick={() => setRecentOrdersModal({ storeName, orders: recentOrders })}>
                {formatNumber(avgOrderAmount, 0)}원 ({recentOrders.length})
              </Button>
            );
          }
          return `${formatNumber(avgOrderAmount, 0)}원`;
        },
      },
      {
        headerName: '월 매출 규모(만원)',
        field: 'monthlyRevenue',
        width: 160,
        type: 'numericColumn',
        editable: true,
        valueFormatter: ({ value }) => {
          if (value == null) return '';
          if (value >= 10000) {
            const eok = value / 10000;
            return `${Number.isInteger(eok) ? eok : parseFloat(eok.toFixed(1))}억`;
          }
          return `${formatNumber(value, 0)}만원`;
        },
        valueSetter: (params) =>
          onValueSetterWithValidation(params, undefined, undefined, { nullable: true, nullify: true }),
        onCellValueChanged: (params) =>
          onCellValueChanged(params, STORE_SALES_INFO_COLLECTION, undefined, undefined, false, true),
      },
      {
        headerName: '등급',
        field: 'storeGrade',
        width: 90,
        cellRenderer: StoreGradeCellRenderer,
        filter: 'agSetColumnFilter',
      },
      {
        headerName: '고가치',
        field: 'vipNote',
        width: 200,
        wrapText: true,
        autoHeight: true,
        cellStyle: { whiteSpace: 'pre-wrap', lineHeight: '1.5', padding: 8 } as any,
        editable: true,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        tooltipField: 'vipNote',
        valueSetter: (params) =>
          onValueSetterWithValidation(params, undefined, undefined, { nullable: true, nullify: true }),
        onCellValueChanged: (params) =>
          onCellValueChanged(params, STORE_SALES_INFO_COLLECTION, undefined, undefined, false, true),
      },
      {
        headerName: '지난달 총 주문금액',
        field: 'recentMonthlyTotalAmount',
        width: 160,
        valueFormatter: formatNumberForAgCell,
      },
      {
        headerName: '액션',
        cellRenderer: DownloadRawDataCell,
      },
    ],
    [salesConfig, handleOpenStoreDetail]
  );

  const handleGridReady = useCallback(() => {
    if (gridRef) {
      loadGridState(gridRef, SALES_MANAGEMENT_TABLE_ID);
    }
  }, [gridRef]);

  // salesConfig 변경 시 관련 셀 렌더러를 강제 리프레시 (AG Grid가 cellRendererParams 변경을 자동 반영하지 않음)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (gridRef?.api) {
      gridRef.api.refreshCells({
        force: true,
        columns: ['setupStatus', 'leadSource', 'leadSourceDetail', 'leadSourceAgent'],
      });
    }
  }, [gridRef, salesConfig]);

  return (
    <div className='height100 flexColumn' style={{ gap: 8 }}>
      <div className='flexRow flexAlignCenter flexSpaceBetween'>
        {toolbarLeftContent}
        <div className='flexRow flexAlignCenter' style={{ gap: 8 }}>
          <AgGridSettingsButtons gridRef={gridRef} tableId={SALES_MANAGEMENT_TABLE_ID} saveSlotCount={3} />
          {toolbarRightContent}
        </div>
      </div>
      <div className='ag-theme-alpine height100 width100'>
        <AgGridReact
          ref={setGridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={handleGridReady}
          enableCellTextSelection
          tooltipShowDelay={500}
          getRowId={(params) => params.data._id}
          getRowStyle={getRowStyle}
        />
      </div>

      <Modal
        title={`최근 주문 내역 - ${recentOrdersModal?.storeName ?? ''}`}
        open={!!recentOrdersModal}
        onCancel={handleCloseRecentOrders}
        footer={null}
        width={400}>
        <Table
          dataSource={recentOrdersModal?.orders}
          columns={recentOrderColumns}
          rowKey='date'
          pagination={false}
          size='small'
          summary={(data) => {
            const total = data.reduce((sum, order) => sum + order.amount, 0);
            const avg = data.length > 0 ? Math.round(total / data.length) : 0;
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <strong>평균 (총 {data.length}건)</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align='right'>
                  <strong>{formatNumber(avg, 0)}원</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Modal>

      <StoreDetailModal
        open={!!storeDetailModal}
        storeName={storeDetailModal?.storeName ?? ''}
        storeId={storeDetailModal?.storeId ?? ''}
        onClose={handleCloseStoreDetail}
      />
    </div>
  );
};

export default SalesManagementTable;
